import {
  retrievePersistentData,
  storePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';

/**
 * The measured bps is consumed by getPlayerConfig as voPlayer's
 * `advanced.startupBitrate`, which seeds the quality the player opens on.
 *
 * The measurement only runs at startup in the browser. On TV the stored value
 * comes from the player's BitrateChanged event instead, so the first launch
 * after install falls back to getPlayerConfig's mid-ladder seed.
 *
 * voPlayer picks the highest representation whose MPD `bandwidth` is <= the
 * value (dash.js getQualityForBitrate). Below the lowest representation it
 * falls back to the lowest one. Only the initial pick is affected — ABR takes
 * over as soon as it has its own throughput samples.
 *
 * The player itself has no resolution ceiling — it selects from whatever
 * representations the manifest offers, so a higher tier would be picked up
 * automatically if one were ever published.
 *
 * Ladders are per-stream. One observed live channel offered these seven:
 *
 *   measured bps           picks  resolution
 *   < 974.000              V0     400x224
 *   974.000 - 1.469.999    V1     512x288
 *   1.470.000 - 2.480.999  V2     854x480
 *   2.481.000 - 3.769.999  V3     1024x576
 *   3.770.000 - 5.941.999  V4     1024x576
 *   5.942.000 - 9.417.999  V5     1280x720@50
 *   >= 9.418.000           V6     1920x1080@50
 *
 * On that ladder a 177 Mbps reading (176.991.150) opens on V6, as does
 * anything from 9.418.000 up — measurement precision only affects the outcome
 * below the top rung.
 * 1920x1080 is the top resolution that the player currently supports
 */
const NETWORK_SPEED_KEY = 'networkSpeed-norlysplay';
const TEST_URL = 'https://speed.cloudflare.com/__down?bytes=1000000';
const TIMEOUT_MS = 15_000;

export interface NetworkSpeedResult {
  bps: number;
  bytes: number;
  ms: number;
  usedResourceTiming: boolean;
}

const getResourceTiming = (url: string): PerformanceResourceTiming | undefined => {
  if (!performance.getEntriesByName) return undefined;

  const entry = performance.getEntriesByName(url).pop() as PerformanceResourceTiming | undefined;

  return entry && entry.responseEnd > entry.responseStart ? entry : undefined;
};

export const measureNetworkSpeed = (url: string = TEST_URL): Promise<NetworkSpeedResult | null> => {
  const requestUrl = `${url}${url.indexOf('?') === -1 ? '?' : '&'}_=${Date.now()}`;

  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    const start = performance.now();

    request.open('GET', requestUrl, true);
    request.responseType = 'arraybuffer';
    request.timeout = TIMEOUT_MS;

    request.onload = () => {
      const wallClockMs = performance.now() - start;

      if (request.status < 200 || request.status >= 300) {
        resolve(null);
        return;
      }

      const entry = getResourceTiming(requestUrl);
      const transferMs = entry ? entry.responseEnd - entry.responseStart : wallClockMs;
      const body = request.response as ArrayBuffer | null;
      const bytes = (entry && entry.encodedBodySize) || (body && body.byteLength) || 0;

      if (!bytes || transferMs <= 0) {
        resolve(null);
        return;
      }

      resolve({
        bps: (bytes * 8) / (transferMs / 1000),
        bytes,
        ms: transferMs,
        usedResourceTiming: !!entry
      });
    };

    request.onerror = () => resolve(null);
    request.ontimeout = () => resolve(null);
    request.onabort = () => resolve(null);

    request.send();
  });
};

export const setNetworkSpeed = (bps: number): void => {
  storePersistentData(NETWORK_SPEED_KEY, Math.round(bps), false);
};

export const storeNetworkSpeed = async (url?: string): Promise<NetworkSpeedResult | null> => {
  const result = await measureNetworkSpeed(url);

  if (result) {
    setNetworkSpeed(result.bps);
  }

  return result;
};

export const getNetworkSpeed = (): number | null =>
  retrievePersistentData<number>(NETWORK_SPEED_KEY);
